import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Truck, Store, Handshake, MapPin } from "lucide-react";
import { PublicHeader, Footer } from "../components/PublicChrome";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SampleBadge } from "../components/Badges";
import { api, errMsg, useApi } from "../lib/api";

const BRAND = { "neat-fufu": "neat-foods", "neat-banku": "neat-foods", "this-way": "atona-foods", "u2-salt": "u2-salt", "special-ice": "special-ice" };

const OrderForm = ({ product, onDone }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", location: "", quantity: 1, channel: "delivery", note: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/public/orders", { ...f, product_id: product.id, quantity: Number(f.quantity), email: f.email || null });
      toast.success(`Order request ${r.data.ref} sent to the ${r.data.routed_to}`);
      onDone(r.data.ref);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="order-form">
      <input required className="field" placeholder="Full name / business name" value={f.name} onChange={set("name")} data-testid="order-name" />
      <div className="grid grid-cols-2 gap-3">
        <input required className="field" placeholder="Phone (MoMo / WhatsApp)" value={f.phone} onChange={set("phone")} data-testid="order-phone" />
        <input type="email" className="field" placeholder="Email (optional)" value={f.email} onChange={set("email")} data-testid="order-email" />
      </div>
      <input required className="field" placeholder="Town / area for delivery or pickup" value={f.location} onChange={set("location")} data-testid="order-location" />
      <div className="grid grid-cols-2 gap-3">
        <input required type="number" min="1" className="field" value={f.quantity} onChange={set("quantity")} data-testid="order-quantity" />
        <select className="field" value={f.channel} onChange={set("channel")} data-testid="order-channel"><option value="delivery">Delivery</option><option value="pickup">Pickup</option><option value="wholesale">Wholesale / bulk</option></select>
      </div>
      <textarea rows={2} className="field" placeholder="Note (pack size, schedule…)" value={f.note} onChange={set("note")} data-testid="order-note" />
      <button className="btn-gold" data-testid="order-submit-btn">Send order request</button>
      <p className="text-[11px] text-ink-3">Prices, pack sizes and payment are confirmed by the brand's sales team by phone. Online payment: not yet connected.</p>
    </form>
  );
};

const PartnerForm = () => {
  const blank = { company: "", contact: "", phone: "", email: "", type: "distributor", region: "", brands: "", note: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/public/suppliers", f);
      toast.success(`Application ${r.data.ref} received — the DGC partnerships desk will contact you.`);
      setF(blank);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="panel p-6 grid gap-3" data-testid="partner-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required className="field" placeholder="Company name" value={f.company} onChange={set("company")} data-testid="partner-company" />
        <input required className="field" placeholder="Contact person" value={f.contact} onChange={set("contact")} data-testid="partner-contact" />
        <input required className="field" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="partner-phone" />
        <input required type="email" className="field" placeholder="Email" value={f.email} onChange={set("email")} data-testid="partner-email" />
        <select className="field" value={f.type} onChange={set("type")} data-testid="partner-type">
          <option value="distributor">Distributor</option><option value="retailer">Retailer / shop</option><option value="online_shop">Online / virtual shop</option><option value="supplier">Supplier (raw materials, packaging, services)</option>
        </select>
        <input required className="field" placeholder="Region (e.g. Ashanti, Greater Accra)" value={f.region} onChange={set("region")} data-testid="partner-region" />
      </div>
      <input className="field" placeholder="Brands of interest (Neat Foods, This Way, U2 Salt, Special Ice…)" value={f.brands} onChange={set("brands")} data-testid="partner-brands" />
      <textarea rows={2} className="field" placeholder="Tell us about your business" value={f.note} onChange={set("note")} data-testid="partner-note" />
      <button className="btn-gold" data-testid="partner-submit-btn">Apply to partner with DGC</button>
    </form>
  );
};

export default function Marketplace() {
  const { data } = useApi("/public/products");
  const [product, setProduct] = useState(null);
  const brands = [...new Set((data || []).map((p) => p.brand))];
  return (
    <div data-testid="marketplace-page">
      <PublicHeader />
      <section className="bg-brand-deep text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="eyebrow !text-[color:var(--gold-hi)] mb-4">DGC Marketplace</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl leading-[1.05]">Made by DESPITE. <span className="gold-text">Delivered to Ghana.</span></h1>
          <p className="text-white/85 mt-5 max-w-2xl">Every DGC product line in one virtual shop — order for home or business, find where to buy, or join our network of suppliers, distributors and retailers.</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[[ShoppingBag, "Product lines", "#lines"], [MapPin, "Where to buy", "#where"], [Store, "Virtual shop", "#lines"], [Handshake, "Suppliers & distributors", "#partners"]].map(([I, l, h]) => (
              <a key={l} href={h} className="bg-white/10 hover:bg-white/20 border border-[color:var(--gold-hi)]/40 rounded-md p-4 transition-colors" data-testid={`shop-jump-${l.toLowerCase().replace(/\W+/g, "-")}`}><I size={18} className="text-[color:var(--gold-hi)]" /><div className="text-sm font-semibold mt-2">{l}</div></a>
            ))}
          </div>
        </div>
      </section>

      <section id="lines" className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="product-lines">
        <div className="eyebrow mb-3">Product lines</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-10">Shop by brand</h2>
        {brands.map((b) => (
          <div key={b} className="mb-12">
            <div className="flex items-center justify-between border-b-2 border-[color:var(--gold-hi)] pb-2 mb-5">
              <h3 className="font-display text-2xl font-bold text-ink">{b}</h3>
              <Link to={`/entities/${BRAND[(data || []).find((p) => p.brand === b).id]}`} className="text-sm text-brand-green hover:underline" data-testid={`shop-brand-page-${b.toLowerCase().replace(/\W+/g, "-")}`}>Brand page →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(data || []).filter((p) => p.brand === b).map((p) => (
                <div key={p.id} id={p.id} className="panel panel-hover overflow-hidden" data-testid={`shop-product-${p.id}`}>
                  <div className="h-52 relative bg-white"><img src={p.image} alt={p.name} className="w-full h-full object-contain p-3" />{p.is_sample_image && <div className="absolute top-2 left-2"><SampleBadge label="Illustration" /></div>}</div>
                  <div className="p-5">
                    <div className="font-display text-lg font-bold text-ink">{p.name}</div>
                    <div className="text-xs text-ink-3">{p.entity} · {p.category}</div>
                    <div className="text-xs text-ink-3 mt-2">Price & pack sizes: confirmed on order</div>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setProduct(p)} className="btn-gold flex-1" data-testid={`shop-order-btn-${p.id}`}><Truck size={14} /> Order</button>
                      <a href="#where" className="btn-ghost" data-testid={`shop-where-btn-${p.id}`}>Where to buy</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section id="where" className="bg-[#F4F8F5] border-y border-[color:var(--line)]" data-testid="where-to-buy">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid lg:grid-cols-3 gap-6">
          <div>
            <div className="eyebrow mb-3">Where to buy</div>
            <h2 className="font-display text-3xl font-bold text-ink">Stockists & virtual shops</h2>
            <p className="text-sm text-ink-3 mt-3">The official list of DGC distributors, supermarkets and online shops was not found publicly. Approved partners from the form below will be listed here by region.</p>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {["Greater Accra", "Ashanti", "Northern regions", "Online / virtual shops"].map((r) => (
              <div key={r} className="panel p-5" data-testid={`region-${r.toLowerCase().replace(/\W+/g, "-")}`}>
                <div className="flex items-center gap-2"><MapPin size={15} className="text-brand-green" /><span className="font-semibold text-ink">{r}</span></div>
                <p className="text-xs text-ink-3 mt-2">[Approved stockists to be listed — not found publicly]</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="partners" className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid lg:grid-cols-5 gap-8" data-testid="partners-section">
        <div className="lg:col-span-2">
          <div className="eyebrow mb-3">Suppliers & distributors</div>
          <h2 className="font-display text-3xl font-bold text-ink">Grow with DGC</h2>
          <ul className="mt-5 space-y-3 text-sm text-ink-2">
            <li><b className="text-brand-green">Distributors</b> — carry Neat Foods, This Way, U2 Salt and Special Ice in your region.</li>
            <li><b className="text-brand-green">Retailers & virtual shops</b> — stock DGC products in-store or online.</li>
            <li><b className="text-brand-green">Suppliers</b> — raw materials, packaging, logistics and services for DGC factories.</li>
          </ul>
        </div>
        <div className="lg:col-span-3"><PartnerForm /></div>
      </section>

      <Dialog open={!!product} onOpenChange={(o) => !o && setProduct(null)}>
        <DialogContent className="bg-white" data-testid="order-dialog">
          <DialogHeader><DialogTitle className="font-display text-brand-green">Order {product?.name}</DialogTitle></DialogHeader>
          {product && <OrderForm product={product} onDone={() => setProduct(null)} />}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
